import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ChevronDown, Bookmark, MapPin, History, LayoutDashboard, Menu, Home, HelpCircle, LogOut, LogIn, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const Navbar = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 8);
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(max > 0 ? Math.min(1, Math.max(0, y / max)) : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      console.log('[Navbar] Initial session:', session?.user?.id, 'isAnonymous:', session?.user?.is_anonymous);
      setUser(session?.user ?? null);
      setIsAnonymous(session?.user?.is_anonymous === true);
    };
    
    getInitialSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[Navbar] Auth state change:', event, 'user:', session?.user?.id, 'isAnonymous:', session?.user?.is_anonymous);
      setUser(session?.user ?? null);
      setIsAnonymous(session?.user?.is_anonymous === true);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleSignOut = async () => {
    console.log('[Navbar] Signing out...');
    // Immediately update UI state
    setUser(null);
    setIsAnonymous(false);
    setMobileMenuOpen(false);
    
    try {
      // Use scope: 'local' to ensure only this tab's session is cleared
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) {
        console.error('[Navbar] Sign out error:', error);
      } else {
        console.log('[Navbar] Sign out successful');
      }
    } catch (err) {
      console.error('[Navbar] Sign out exception:', err);
    }
    
    // Always navigate to home after logout attempt
    navigate('/');
  };

  const handleNavigate = (path: string) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  // Show profile access if user is authenticated and not anonymous
  const showProfileAccess = user && !isAnonymous;

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background/70 backdrop-blur-xl border-b border-border/60 shadow-soft'
          : 'bg-background/40 backdrop-blur-md border-b border-border/30'
      }`}
    >
      <nav className="container mx-auto px-4 py-4 flex items-center justify-between">
        <Link
          to="/"
          onClick={() => window.scrollTo({ top: 0, behavior: 'auto' })}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          <img src="/logo-mark.png" alt="TripVerse logo" className="h-8 w-8" width="32" height="32" fetchPriority="high" decoding="async" />
          <span className="text-xl font-bold text-primary">
            TripVerse
          </span>
        </Link>
        
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="sm">Home</Button>
          </Link>
          <Link to="/quiz">
            <Button variant="accent" size="sm">Quiz</Button>
          </Link>
          
          {showProfileAccess ? (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-1">
                    <User className="h-4 w-4 mr-1" />
                    Account
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-background">
                  <DropdownMenuItem onClick={() => navigate('/profile?tab=saved')}>
                    <Bookmark className="mr-2 h-4 w-4" />
                    Saved
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile?tab=visited')}>
                    <MapPin className="mr-2 h-4 w-4" />
                    Visited
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile?tab=history')}>
                    <History className="mr-2 h-4 w-4" />
                    History
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile?tab=stats')}>
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    Overview
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="ghost" onClick={handleSignOut}>
                Logout
              </Button>
            </>
          ) : (
            <Link to="/auth">
              <Button variant="outline">Login</Button>
            </Link>
          )}
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="flex flex-col gap-2 mt-8">
                <Button 
                  variant="ghost" 
                  className="justify-start gap-3 h-12"
                  onClick={() => handleNavigate('/')}
                >
                  <Home className="h-5 w-5" />
                  Home
                </Button>
                <Button 
                  variant="accent" 
                  className="justify-start gap-3 h-12"
                  onClick={() => handleNavigate('/quiz')}
                >
                  <HelpCircle className="h-5 w-5" />
                  Take Quiz
                </Button>
                
                {showProfileAccess ? (
                  <>
                    <div className="border-t border-border my-2" />
                    <p className="text-xs text-muted-foreground px-4 py-2 uppercase tracking-wider">
                      Account
                    </p>
                    <Button 
                      variant="ghost" 
                      className="justify-start gap-3 h-12"
                      onClick={() => handleNavigate('/profile?tab=saved')}
                    >
                      <Bookmark className="h-5 w-5" />
                      Saved
                    </Button>
                    <Button 
                      variant="ghost" 
                      className="justify-start gap-3 h-12"
                      onClick={() => handleNavigate('/profile?tab=visited')}
                    >
                      <MapPin className="h-5 w-5" />
                      Visited
                    </Button>
                    <Button 
                      variant="ghost" 
                      className="justify-start gap-3 h-12"
                      onClick={() => handleNavigate('/profile?tab=history')}
                    >
                      <History className="h-5 w-5" />
                      History
                    </Button>
                    <Button 
                      variant="ghost" 
                      className="justify-start gap-3 h-12"
                      onClick={() => handleNavigate('/profile?tab=stats')}
                    >
                      <LayoutDashboard className="h-5 w-5" />
                      Overview
                    </Button>
                    <div className="border-t border-border my-2" />
                    <Button 
                      variant="ghost" 
                      className="justify-start gap-3 h-12 text-destructive hover:text-destructive"
                      onClick={handleSignOut}
                    >
                      <LogOut className="h-5 w-5" />
                      Logout
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="border-t border-border my-2" />
                    <Button 
                      variant="outline" 
                      className="justify-start gap-3 h-12"
                      onClick={() => handleNavigate('/auth')}
                    >
                      <LogIn className="h-5 w-5" />
                      Login
                    </Button>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
      {/* Scroll progress indicator */}
      <div
        className="h-0.5 bg-highlight origin-left transition-transform duration-150 ease-out"
        style={{ transform: `scaleX(${scrollProgress})` }}
        aria-hidden="true"
      />
    </header>
  );
};

export default Navbar;